import fetchGraphQL from '@github-ui/fetch-graphql'
import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {useQueryClient} from '@github-ui/react-query'
import type {AddToProjectsBulkActionQueryQuery} from '@github-ui/server-persisted-queries/types/AddToProjectsBulkActionQuery'
import {ProjectSymlinkIcon} from '@primer/octicons-react'
import {useCallback, useState} from 'react'

import {RepoIssuesBulkPickerAnchor, type RepoIssuesBulkActionProps} from './RepoIssuesBulkActionShared'

const ADD_TO_PROJECTS_QUERY_ID = '4d5b7b6b0504e1bf68c28883ac8e3f8d'
const ADD_TO_PROJECTS_QUERY_NAME = 'AddToProjectsBulkActionQuery'

async function fetchIssueProjects(issueIds: readonly string[]): Promise<AddToProjectsBulkActionQueryQuery> {
  const response = await fetchGraphQL(ADD_TO_PROJECTS_QUERY_ID, ADD_TO_PROJECTS_QUERY_NAME, {ids: issueIds}, 'GET')
  if (
    Array.isArray(response) ||
    ('errors' in response && response.errors?.length) ||
    !('data' in response) ||
    !response.data
  ) {
    throw new Error('Bulk project query returned errors')
  }
  return response.data as AddToProjectsBulkActionQueryQuery
}

function projectsAppliedToAll(data: AddToProjectsBulkActionQueryQuery): ProjectPickerBaseProject[] {
  const projectsByIssue = data.nodes.map(node =>
    node?.__typename === 'Issue'
      ? (node.projectItems?.edges ?? []).flatMap(edge => (edge?.node?.project ? [edge.node.project] : []))
      : [],
  )
  const firstIssueProjects = projectsByIssue[0] ?? []
  return firstIssueProjects.filter(project =>
    projectsByIssue.every(issueProjects => issueProjects.some(candidate => candidate.id === project.id)),
  )
}

export function RepoIssuesBulkProjects({
  issueIds,
  ...props
}: RepoIssuesBulkActionProps & {issueIds: readonly string[]}) {
  const selectionKey = JSON.stringify([...issueIds].sort())
  return <RepoIssuesBulkProjectsForSelection key={selectionKey} issueIds={issueIds} {...props} />
}

function RepoIssuesBulkProjectsForSelection({
  disabled,
  nested,
  owner,
  repo,
  issueIds,
  onUpdate,
}: RepoIssuesBulkActionProps & {issueIds: readonly string[]}) {
  const queryClient = useQueryClient()
  const [selectedProjects, setSelectedProjects] = useState<ProjectPickerBaseProject[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [queryError, setQueryError] = useState<Error | null>(null)
  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => (
      <RepoIssuesBulkPickerAnchor
        {...{anchorProps, disabled: disabled || isLoading, nested}}
        icon={ProjectSymlinkIcon}
        label="Project"
      />
    ),
    [disabled, isLoading, nested],
  )
  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('projects'),
    anchorElement,
    open: true,
  })
  const loadProjects = useCallback(async () => {
    if (disabled || isLoading) return
    setIsLoading(true)
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['repo-issues-bulk-projects', {issueIds}],
        queryFn: () => fetchIssueProjects(issueIds),
      })
      setSelectedProjects(projectsAppliedToAll(data))
    } catch (error) {
      setQueryError(error instanceof Error ? error : new Error('Failed to load projects'))
    } finally {
      setIsLoading(false)
    }
  }, [disabled, isLoading, issueIds, queryClient])

  if (queryError) {
    return createFallbackComponent(() => {
      setQueryError(null)
      void loadProjects()
    })
  }

  if (!selectedProjects) {
    return anchorElement({
      onClick: () => {
        void loadProjects()
      },
    })
  }

  return (
    <ProjectPicker
      pickerId="repo-issues-bulk-projects"
      anchorElement={anchorElement}
      title="Add to projects"
      repo={repo}
      owner={owner}
      readonly={disabled}
      selectedProjects={selectedProjects}
      onSave={(projects: ProjectPickerBaseProject[]) => {
        const addToProjectIds = projects
          .filter(project => !selectedProjects.some(selected => selected.id === project.id))
          .map(project => project.id)
        const removeFromProjectIds = selectedProjects
          .filter(project => !projects.some(selected => selected.id === project.id))
          .map(project => project.id)
        if (addToProjectIds.length > 0 || removeFromProjectIds.length > 0) {
          onUpdate({addToProjectIds, removeFromProjectIds})
        }
      }}
      nested={nested}
      triggerOpen
    />
  )
}
